import React, { useState, useEffect, useMemo, useRef } from "react";
import { DatePicker } from "antd";

import dayjs from "dayjs";
import { Box, Typography, Stack, Button, Fade, Dialog, DialogContent, DialogActions } from "@mui/material";
import {
  getTicketStatusReport,
  getTicketPriorityReport,
  getTicketChannelReport,
  getSlaReport,
  getDepartmentReport,
  getCategoryReport,
} from "../../services/report.service";
// removed unused icons
import StatusMetrics from "../../components/ReportCategory/StatusMetrics";
import PrioritySlaCharts from "../../components/ReportCategory/PrioritySlaCharts";
import TicketChannelTable from "../../components/ReportCategory/TicketChannelTable";
import CategoryTotalsTable from "../../components/ReportCategory/CategoryTotalsTable";
import CaseHistoryReportTable from "../../components/ReportCategory/CaseHistoryReportTable";
import DepartmentDailyGrid from "../../components/ReportCategory/DepartmentDailyGrid";
import { getCaseHistoryCount } from "../../services/caseHistory.service";
// removed unused DepartmentReport/DepartmentDatePicker imports
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import styles from "./ReportCategory.module.scss";

const { RangePicker } = DatePicker;

const ReportCategory = () => {
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));

  const [departmentStartDate, setDepartmentStartDate] = useState(
    dayjs().startOf("month")
  );
  const [departmentEndDate, setDepartmentEndDate] = useState(
    dayjs().endOf("month")
  );

  const [ticketStatusData, setTicketStatusData] = useState({});
  const [loadingTicketStatus, setLoadingTicketStatus] = useState(false);

  const [completedTicketStatusData, setCompletedTicketStatusData] = useState(
    {}
  );
  const [loadingCompletedTicketStatus, setLoadingCompletedTicketStatus] =
    useState(false);

  const [ticketPriorityData, setTicketPriorityData] = useState({});
  const [loadingTicketPriority, setLoadingTicketPriority] = useState(false);

  const [ticketChannelData, setTicketChannelData] = useState([]);
  const [loadingTicketChannel, setLoadingTicketChannel] = useState(false);

  const [slaReportData, setSlaReportData] = useState([]);
  const [loadingSlaReport, setLoadingSlaReport] = useState(false);

  const [departmentReportData, setDepartmentReportData] = useState([]);

  const [loadingDepartmentReport, setLoadingDepartmentReport] = useState(false);

  const [categoryReportData, setCategoryReportData] = useState([]);
  const [loadingCategoryReport, setLoadingCategoryReport] = useState(false);

  const [caseHistoryReportData, setCaseHistoryReportData] = useState([]);
  const [loadingCaseHistoryReport, setLoadingCaseHistoryReport] = useState(false);



  const handleDateChange = (dates) => {
    if (dates) {
      setStartDate(dates[0]);
      setEndDate(dates[1]);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleDepartmentDateChange = (dates) => {
    if (dates) {
      setDepartmentStartDate(dates[0]);
      setDepartmentEndDate(dates[1]);
    } else {
      setDepartmentStartDate(null);
      setDepartmentEndDate(null);
    }
  };

  const fetchReport = async (
    serviceFn,
    setData,
    setLoading,
    startDate,
    endDate,
  ) => {
    setLoading(true);
    try {
      const response = await serviceFn({
        startDate: startDate.format("DD-MM-YYYY"),
        endDate: endDate.format("DD-MM-YYYY"),
      });
      setData(response.data || {});
    } catch (error) {
      console.error("Error fetching report:", error);
      setData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(
      getTicketStatusReport,
      setTicketStatusData,
      setLoadingTicketStatus,
      startDate,
      endDate
    );
    fetchReport(
      getSlaReport,
      setCompletedTicketStatusData,
      setLoadingCompletedTicketStatus,
      startDate,
      endDate
    );
    fetchReport(
      getTicketPriorityReport,
      setTicketPriorityData,
      setLoadingTicketPriority,
      startDate,
      endDate
    );
    // Default server-side sorting for Ticket Channel Report: closed without desc
    setLoadingTicketChannel(true);
    getTicketChannelReport({
      startDate: startDate.format("DD-MM-YYYY"),
      endDate: endDate.format("DD-MM-YYYY"),
      scope: "closed",
      order: "desc",
    })
      .then((response) => setTicketChannelData(response.data || []))
      .catch((e) => {
        console.error("Error fetching ticket channel:", e);
        setTicketChannelData([]);
      })
      .finally(() => setLoadingTicketChannel(false));
    fetchReport(
      getSlaReport,
      setSlaReportData,
      setLoadingSlaReport,
      startDate,
      endDate
    );
    // Default server-side sorting for Category Report: closed without desc
    setLoadingCategoryReport(true);
    getCategoryReport({
      startDate: startDate.format("DD-MM-YYYY"),
      endDate: endDate.format("DD-MM-YYYY"),
      scope: "closed",
      order: "desc",
    })
      .then((response) => setCategoryReportData(response.data || {}))
      .catch((e) => {
        console.error("Error fetching category report:", e);
        setCategoryReportData({});
      })
      .finally(() => setLoadingCategoryReport(false));

    // Fetch Case History Report
    setLoadingCaseHistoryReport(true);
    getCaseHistoryCount({
      startDate: startDate.format("DD-MM-YYYY"),
      endDate: endDate.format("DD-MM-YYYY"),
    })
      .then((response) => {
        const actualData = response.data?.data || response.data || [];
        setCaseHistoryReportData(Array.isArray(actualData) ? actualData : []);
      })
      .catch((e) => {
        console.error("Error fetching case history report:", e);
        setCaseHistoryReportData([]);
      })
      .finally(() => setLoadingCaseHistoryReport(false));
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReport(
      getDepartmentReport,
      (data) => setDepartmentReportData(Array.isArray(data) ? data : []),
      setLoadingDepartmentReport,
      departmentStartDate,
      departmentEndDate,
    );
  }, [departmentStartDate, departmentEndDate]);



  const { departmentPdfColumns, departmentTableData } = useMemo(() => {
    const departmentDateColumns = [];
    const departmentPdfDateColumns = [];
    if (departmentStartDate && departmentEndDate) {
      let currentDate = dayjs(departmentStartDate);
      while (
        currentDate.isBefore(departmentEndDate) ||
        currentDate.isSame(departmentEndDate)
      ) {
        departmentDateColumns.push({
          id: currentDate.format("DD-MM-YYYY"),
          label: currentDate.format("DD-MM-YYYY"), // Full date for web
          accessorKey: currentDate.format("DD-MM-YYYY"),
        });
        departmentPdfDateColumns.push({
          id: currentDate.format("DD-MM-YYYY"),
          label: currentDate.format("D"), // Day only for PDF
          accessorKey: currentDate.format("DD-MM-YYYY"),
        });
        currentDate = currentDate.add(1, "day");
      }
    }

    const departmentColumns = [
      { id: "description", label: "Description", accessorKey: "description" },
      ...departmentDateColumns,
      { id: "total", label: "Total", accessorKey: "total" },
    ];

    const departmentPdfColumns = [
      { id: "description", label: "Description", accessorKey: "description" },
      ...departmentPdfDateColumns,
      { id: "total", label: "Total", accessorKey: "total" },
    ];

    const statuses = [
      { key: "unassigned", label: "Unassigned" },
      { key: "assigned", label: "Assigned" },
      { key: "reassigned", label: "Reassigned" },
      { key: "completed", label: "Completed" },
    ];

    const departmentTableData = statuses.map((status) => {
      const row = { description: status.label };
      let total = 0;

      departmentDateColumns.forEach((col) => {
        const dateData = departmentReportData?.find(
          (d) => d._id === col.accessorKey
        );
        const value = dateData ? dateData[status.key] || 0 : 0;
        row[col.accessorKey] = value;
        total += value;
      });

      row.total = total;
      return row;
    });

    return { departmentColumns, departmentPdfColumns, departmentTableData };
  }, [departmentReportData, departmentStartDate, departmentEndDate]);

  const handleDownloadPdf = () => {
    const doc = new jsPDF();
    doc.text("Reports", 14, 16);
    let yPos = 22;

    // Ticket Status Report
    autoTable(doc, {
      startY: yPos,
      head: [["Ticket Status Report", "Value"]],
      body: [
        ["Total Assigned Cases", ticketStatusData.totalAssignedCases || 0],
        ["On Time Assigned Cases", ticketStatusData.onTimeAssignedCases || 0],
        [
          "Not On Time Assigned Cases",
          ticketStatusData.notOnTimeAssignedCases || 0,
        ],
      ],
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // Ticket Completion Status Report
    autoTable(doc, {
      startY: yPos,
      head: [["Ticket Completion Status Report", "Value"]],
      body: [
        [
          "Total Completed Cases",
          (completedTicketStatusData?.closed?.within || 0) +
          (completedTicketStatusData?.closed?.without || 0) || 0,
        ],
        [
          "On Time Completed Cases",
          completedTicketStatusData?.closed?.within || 0,
        ],
        [
          "Not On Time Completed Cases",
          completedTicketStatusData?.closed?.without || 0,
        ],
      ],
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
    });
    yPos = doc.lastAutoTable.finalY + 10;
    // Ticket Priority Report
    autoTable(doc, {
      startY: yPos,
      head: [["Ticket Priority Report", "Total"]],
      body: ticketPriorityTableData.map((row) => [row.description, row.count]),
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // SLA Report
    autoTable(doc, {
      startY: yPos,
      head: [["SLA Report", "Within SLA", "Without SLA", "Total"]],
      body: [
        [
          "Open",
          slaReportData.open?.within ?? 0,
          slaReportData.open?.without ?? 0,
          (slaReportData.open?.within ?? 0) +
          (slaReportData.open?.without ?? 0),
        ],
        [
          "Closed",
          slaReportData.closed?.within ?? 0,
          slaReportData.closed?.without ?? 0,
          (slaReportData.closed?.within ?? 0) +
          (slaReportData.closed?.without ?? 0),
        ],
        [
          "Total",
          (slaReportData.open?.within ?? 0) +
          (slaReportData.closed?.within ?? 0),
          (slaReportData.open?.without ?? 0) +
          (slaReportData.closed?.without ?? 0),
          (slaReportData.open?.within ?? 0) +
          (slaReportData.open?.without ?? 0) +
          (slaReportData.closed?.within ?? 0) +
          (slaReportData.closed?.without ?? 0),
        ],
      ],
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // Ticket Channel Report
    autoTable(doc, {
      startY: yPos,
      head: [
        [
          {
            content: "Ticket Channel Report",
            colSpan: 6,
            styles: { halign: "center", fillColor: [22, 160, 133] },
          },
        ],
        [
          { content: "Assigned To", rowSpan: 2 },
          { content: "Position", rowSpan: 2 },
          { content: "Open", colSpan: 2, styles: { halign: "center" } },
          { content: "Closed", colSpan: 2, styles: { halign: "center" } },
        ],
        ["Within", "Without", "Within", "Without"],
      ],
      body: ticketChannelData.map((row) => [
        row.assignedTo,
        row.position,
        row.open.within,
        row.open.without,
        row.closed.within,
        row.closed.without,
      ]),
      theme: "grid",
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // Category Report
    const categoryRows =
      categoryReportData && typeof categoryReportData === "object"
        ? Object.entries(categoryReportData).map(([key, value]) => ({
          description: key,
          openWithin: value.open.within,
          openWithout: value.open.without,
          closedWithin: value.closed.within,
          closedWithout: value.closed.without,
          total:
            value.open.within +
            value.open.without +
            value.closed.within +
            value.closed.without,
        }))
        : [];

    autoTable(doc, {
      startY: yPos,
      head: [
        [
          {
            content: "Category Report",
            colSpan: 6,
            styles: { halign: "center", fillColor: [22, 160, 133] },
          },
        ],
        [
          { content: "Description", rowSpan: 2 },
          { content: "Open", colSpan: 2, styles: { halign: "center" } },
          { content: "Closed", colSpan: 2, styles: { halign: "center" } },
          { content: "Total", rowSpan: 2 },
        ],
        ["Within", "Without", "Within", "Without"],
      ],
      body: categoryRows.map((row) => [
        row.description,
        row.openWithin,
        row.openWithout,
        row.closedWithin,
        row.closedWithout,
        row.total,
      ]),
      theme: "grid",
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // Department Report
    const departmentDateColumns = departmentPdfColumns.filter(
      (c) => c.id !== "description" && c.id !== "total"
    );
    const departmentTableBody = departmentTableData.map((row) => [
      row.description,
      ...departmentDateColumns.map((col) => row[col.accessorKey]),
      row.total,
    ]);

    if (departmentTableBody.length === 0) {
      departmentTableBody.push([
        {
          content: "No data available",
          colSpan: departmentDateColumns.length + 2,
          styles: { halign: "center" },
        },
      ]);
    }

    autoTable(doc, {
      startY: yPos,
      head: [
        ["Description", ...departmentDateColumns.map((c) => c.label), "Total"],
      ],
      body: departmentTableBody,
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
    });

    doc.save("reports.pdf");
  };

  const ticketPriorityTableData = ticketPriorityData
    ? [
      { description: "Assigned", count: ticketPriorityData.assignedCount },
      {
        description: "Reassigned",
        count: ticketPriorityData.reAssignedCount,
      },
      { description: "Completed", count: ticketPriorityData.completedCount },
      { description: "Reject", count: ticketPriorityData.rejectCount },
      {
        description: "Unassigned",
        count: ticketPriorityData.unassignedCount,
      },
    ]
    : [];

  // Intersection Observer for scroll animations
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observers = sectionRefs.current.map((ref, index) => {
      if (!ref) return null;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
            }
          });
        },
        { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
      );

      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => {
        if (observer) observer.disconnect();
      });
    };
  }, [ticketStatusData, ticketPriorityData, slaReportData]);

  return (
    <div className={`mainContent ${styles.reportCategoryContainer}`}>
      <Fade in={true} timeout={600}>
        <Box className={styles.headerSection}>
          <Typography className={styles.headerTitle}>
            Ticket Status Report
          </Typography>
          <Box className={styles.headerActions}>
            <Box className={styles.datePickerWrapper}>
              <RangePicker
                value={[startDate, endDate]}
                onChange={handleDateChange}
                style={{ height: "42px", borderRadius: "8px" }}
                size="large"
              />
            </Box>
            <Button
              className={styles.downloadButton}
              variant="contained"
              onClick={handleDownloadPdf}
            >
              📥 Download PDF
            </Button>
          </Box>
        </Box>
      </Fade>

      {/* New UI Sections with animations */}
      <Box
        className={`${styles.sectionContainer} ${styles.animateOnScroll}`}
        ref={(el) => (sectionRefs.current[0] = el)}
        sx={{ mb: 4 }}
      >
        <StatusMetrics
          ticketStatusData={ticketStatusData}
          loading={loadingTicketStatus}
        />
      </Box>
      <Box
        className={`${styles.sectionContainer} ${styles.animateOnScroll}`}
        ref={(el) => (sectionRefs.current[0] = el)}
        sx={{ mb: 4 }}
      >
        <StatusMetrics
          ticketStatusData={completedTicketStatusData}
          loading={loadingCompletedTicketStatus}
          type={2}
        />
      </Box>
      <Box
        className={`${styles.sectionContainer} ${styles.animateOnScroll}`}
        ref={(el) => (sectionRefs.current[1] = el)}
        sx={{ mb: 4 }}
      >
        <PrioritySlaCharts
          ticketPriorityData={ticketPriorityData}
          slaReportData={slaReportData}
          loading={loadingTicketPriority || loadingSlaReport}
        />
      </Box>

      <Box
        className={`${styles.sectionContainer} ${styles.animateOnScroll}`}
        ref={(el) => (sectionRefs.current[2] = el)}
        sx={{ mb: 4 }}
      >
        <TicketChannelTable
          ticketChannelData={ticketChannelData}
          loading={loadingTicketChannel}
        />
      </Box>

      <Box
        className={`${styles.sectionContainer} ${styles.animateOnScroll}`}
        ref={(el) => (sectionRefs.current[3] = el)}
        sx={{ mb: 4 }}
      >
        <CategoryTotalsTable
          categoryReportData={categoryReportData}
          loading={loadingCategoryReport}
        />
      </Box>

      {/* <Box
        className={`${styles.sectionContainer} ${styles.animateOnScroll}`}
        ref={(el) => (sectionRefs.current[5] = el)}
        sx={{ mb: 4 }}
      >
        <CaseHistoryReportTable
          caseHistoryData={caseHistoryReportData}
          loading={loadingCaseHistoryReport}
          startDate={startDate}
          endDate={endDate}
        />
      </Box> */}

      <Box
        className={`${styles.sectionContainer} ${styles.animateOnScroll}`}
        ref={(el) => (sectionRefs.current[4] = el)}
        sx={{ mb: 4, mt: 4 }}
      >
        <DepartmentDailyGrid
          departmentReportData={departmentReportData}
          departmentStartDate={departmentStartDate}
          departmentEndDate={departmentEndDate}
          loading={loadingDepartmentReport}
          onDepartmentDateChange={handleDepartmentDateChange}
        />
      </Box>



    </div>
  );
};

export default ReportCategory;
